import React, {useState, useEffect, ChangeEvent} from "react";
import axios from "axios";
import {Token, createToken} from "./TokenUtils";
import Property from "./Property";
import Utils from "./Utils";
import {FaEye, FaEyeSlash} from "react-icons/fa";

// Define component state type
interface ProfileState {
  message: string;
  messageColor: string;
  username: string;
  tokens: Token[],
  roles: string[]
  ;
}

const Profile: React.FC = () => {
  const [state, setState] = useState<ProfileState>({
    message: "",
    messageColor: "#f00",
    username: "",
    tokens: [],
    roles: [],
  });

  useEffect(() => {
    fetchProfileInfo();
    fetchTokens();
  }, []);

  const fetchTokens = () => {
    const accessToken = Utils.getAccessToken();
    const refreshToken = Utils.getRefreshToken();
    if (!accessToken && !refreshToken) {
      return;
    }

    // Fetch tokens from /api/tokens; the axios interceptor refreshes the token if expired.
    const config = fetchHeaders();
    axios
      .get(Property.BASE_API_URL + "/api/tokens", config)
      .then((response) => {
        const tokensRaw = response.data
          .map((token: any) => createToken(token))
          .filter((token: Token | null) => token !== null);
        setState((prevState) => ({...prevState, tokens: tokensRaw}));
      })
      .catch(() => {
        setState((prevState) => ({...prevState, tokens: []}));
      });

    if (!accessToken) {
      window.location.replace("/");
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setState((prevState) => ({...prevState, username: event.target.value}));
  };

  const fetchHeaders = () => {
    const site = Property.BASE_API_URL;
    return {
      headers: {
        "Cache-Control": "no-cache",
        "Content-Type": "application/json",
        "Access-Control-Allow-Methods": "GET",
        "Access-Control-Allow-Origin": site,
        Authorization: Utils.generateAuthorizationHeaderValue(),
      },
    };
  };

  const fetchProfileInfo = () => {
    const url = Property.BASE_API_URL + "/api/users/me";
    const config = fetchHeaders();

    axios
      .get(url, config)
      .then((response) => {
        setState((prevState) => ({...prevState, username: response.data.username,
          roles: response.data.roles}));
      })
      .catch((error) => {
        // The interceptor already refreshed the token before the request; a failure
        // here means there is no valid session, so send the user to the login page.
        if (!Utils.getAccessToken()) {
          window.location.replace("/");
        }
        console.log(error);
      });
  };

  const updateUserInfo = () => {
    const headers = fetchHeaders();
    const url = Property.BASE_API_URL + "/api/users";

    const requestBody = {username: state.username};

    axios
      .put(url, requestBody, headers)
      .then(() => {
        setState((prevState) => ({
          ...prevState,
          message: "Update succeeded",
          messageColor: "#0f0",
        }));
      })
      .catch((error) => {
        setState((prevState) => ({
          ...prevState,
          message: `${error.response.status}: ${error.response.data.message}`,
          messageColor: "#f00",
        }));
      });

    setTimeout(() => {
      setState((prevState) => ({...prevState, message: ""}));
    }, Property.MESSAGE_TIMEOUT_MILLISECONDS);
  };

  const toggleTokenVisibility = (index: number) => {
    const updatedTokens = state.tokens.map((token, idx) =>
      idx === index ? {...token, showToken: !token.showToken} : token
    );
    setState((prevState) => ({...prevState, tokens: updatedTokens}));
  };

  const maskToken = (token: string) => {
    if (token.length <= 8) {
      return token; // If the token length is 8 or less, don't mask
    }
    const maskedLength = token.length - 8;
    return token.slice(0, 4) + '*'.repeat(maskedLength) + token.slice(-4);
  };

  const removeAllTokens = () => {
    const config = fetchHeaders();

    axios
      .delete(Property.BASE_API_URL + "/api/tokens", config)
      .then(() => {
        Utils.setAccessToken(null);
        Utils.setRefreshToken(null);
        setState((prevState) => ({ ...prevState, tokens: [] }));
        window.location.replace("/");
      })
      .catch((error) => {
        setState((prevState) => ({
          ...prevState,
          message: `Failed to remove all tokens: ${error.response?.status}: ${error.response?.data?.message || error.message}`,
          messageColor: "#f00",
        }));
      });
  };

  const {message, messageColor, username, tokens} = state;

  return (
    <div className="content-holder">
      <h2>Profile settings</h2>
      <span id="message" style={{color: messageColor}}>
        {message}
      </span>
      <p>Name</p>
      <input
        type="text"
        placeholder="name"
        id="name"
        value={username}
        onChange={handleChange}
      />
      <button onClick={updateUserInfo}>Save</button>
      <p>Roles</p>
      <input id="roles" value={state.roles?.join(", ")} disabled />

      <p>Active tokens</p>
      <table>
        <thead>
        <tr>
          <th style={{width: "320px"}}>JTI (identifier)</th>
          <th>Type</th>
          <th>Valid until</th>
          <th>Actions</th>
        </tr>
        </thead>
        <tbody>
        {tokens.map((token, index) => (
          <tr key={token.jti} style={{ backgroundColor: Utils.isTokenExpired(token) ? Property.COLOR_PINK : undefined }}>
            <td>
              {token.showToken ? token.jti : maskToken(token.jti)}
              <span onClick={() => toggleTokenVisibility(index)}
                    style={{cursor: "pointer", marginLeft: "10px", float: "right"}}>
                  {token.showToken ? <>{FaEyeSlash({})}</> : <>{FaEye({})}</>}
                </span>
            </td>
            <td>{token.type}</td>
            <td>{Utils.getTokenExpirationDate(token)?.toISOString()}</td>
            <td>
              <button
                onClick={() => {
                  Utils.deleteToken(token)
                    .then(() => {
                      setState(prevState => ({
                        ...prevState,
                        tokens: prevState.tokens.filter(t => t.jti !== token.jti)
                      }));

                      if (!Utils.getAccessToken()) {
                        window.location.replace("/");
                      }
                    })
                    .catch(error => {
                      setState(prevState => ({
                        ...prevState,
                        message: `Failed to delete token: ${error.response?.data?.message || error.message}`,
                        messageColor: "#f00"
                      }));
                    });
                }}
              >
                Delete
              </button>
            </td>
          </tr>
        ))}
        </tbody>
      </table>
      {tokens.length > 0 && (
        <button
          onClick={removeAllTokens}
          style={{ marginTop: '10px', backgroundColor: '#ff4444', color: 'white' }}
        >
          Remove All Tokens
        </button>
      )}
      <p>Latest events</p>
      <span>
        (yet it is empty, not filled and not supported, later here will be list of comments, profile updates etc. provided by <strong>Event Sourcing)</strong>
      </span>
      <table>
        <thead>
        <tr>
          <th>Sequence</th>
          <th>Operation</th>
          <th>Request Body</th>
          <th>Result</th>
        </tr>
        </thead>
        <tbody>
        <tr>
          <td>001</td>
          <td>UPDATE</td>
          <td>{"{some json}"}</td>
          <td>200, SUCCESS / PROCESSING / ...</td>
        </tr>
        </tbody>
      </table>
    </div>
  );
};

export default Profile;